import EmptyState from 'components/EmptyState/EmptyState';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import { getForecastDateLabel } from 'features/Weather/utils/forecastDate.utils';
import useLocation from 'hooks/useLocation';
import { useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { useGetDetailedMarine7Query } from 'services/weather/marine/marine.api';
import MarineConditionPanel from '../../components/MarineConditionPanel/MarineConditionPanel';
import BeaufortIcons from './components/BeaufortIcons/BeaufortIcons';
import { route, waveDetails, windDetails } from './config/waveAndWind.config';
import {
  getSelectedWaveAndWindWeekday,
  getWaveAndWindSegmentEntries,
  getWaveAndWindWeekdayEntries,
} from './utils/waveAndWind.utils';
import 'features/Weather/assets/weatherDataDisplay.css';
import './assets/waveAndWind.scss';

const WaveAndWind = ({ selectedDayNum }) => {
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const {
    data: detailedMarine = [],
    isLoading: loadingDetailedMarine,
    isFetching: fetchingDetailedMarine,
    fulfilledTimeStamp,
  } = useGetDetailedMarine7Query(effectiveLocation);
  const isRefreshing = fetchingDetailedMarine && !loadingDetailedMarine; // Only true on subsequent fetches, not the initial load
  const selectedWeekday = getSelectedWaveAndWindWeekday(selectedDayNum);

  const weekdayEntries = useMemo(
    () => getWaveAndWindWeekdayEntries(detailedMarine, selectedWeekday),
    [detailedMarine, selectedWeekday],
  );

  const segmentEntries = useMemo(
    () => getWaveAndWindSegmentEntries(weekdayEntries),
    [weekdayEntries],
  );

  const dateLabel = useMemo(
    () => getForecastDateLabel(weekdayEntries[0]?.iso8601, 'Wave and Wind Conditions'),
    [weekdayEntries],
  );
  const pageHeading = dateLabel ?? `${selectedWeekday} Wave and Wind Conditions`;

  const getCardIcon = (entry) => <BeaufortIcons conditionId={entry.beaufort} />;

  return (
    <Container
      fluid
      className="wave-and-wind weather-data-display d-flex h-100 flex-column gap-3 px-0 text-white user-select-none"
    >
      <WidgetTimeUpdate route={route} updatedAt={fulfilledTimeStamp ?? null} />
      {loadingDetailedMarine ? (
        <div className="d-flex h-100 align-items-center justify-content-center mt-3">
          <WidgetLoadingAnimation />
        </div>
      ) : !weekdayEntries.length ? (
        <EmptyState
          title="Wave and Wind Conditions"
          message="No wave and wind forecast data available for the selected day."
        />
      ) : (
        <>
          <div className="pb-2 border-bottom weather-data-display-heading">
            <h2 className="m-0 fw-bold lh-sm weather-data-display-title">{pageHeading}</h2>
          </div>

          <div className="wave-and-wind-scroll flex-grow-1 overflow-auto">
            <div className="d-flex flex-column gap-4">
              <MarineConditionPanel
                title="Wave"
                isRefreshing={isRefreshing}
                marineData={segmentEntries}
                details={waveDetails}
                hasPanelChrome={false}
                containerClassName="wave-and-wind-section gap-2"
                headerClassName="wave-and-wind-section-header"
                titleClassName="wave-and-wind-section-title fs-5"
                statusClassName="wave-and-wind-section-status"
                cardListClassName="weather-time-period-grid wave-and-wind-card-grid"
                cardContainerClassName="wave-and-wind-time-card"
                emptyMessage={`No wave forecast data available for ${selectedWeekday}.`}
                getCardTitle={(entry) => entry.day_segment}
                getCardSubtitle={(entry) => entry.beaufort_desc}
                getCardIcon={getCardIcon}
                headingLevel="h3"
                cardHeadingLevel="h4"
              />
              <MarineConditionPanel
                title="Wind"
                isRefreshing={isRefreshing}
                marineData={segmentEntries}
                details={windDetails}
                hasPanelChrome={false}
                containerClassName="wave-and-wind-section gap-2"
                headerClassName="wave-and-wind-section-header"
                titleClassName="wave-and-wind-section-title fs-5"
                statusClassName="wave-and-wind-section-status"
                cardListClassName="weather-time-period-grid wave-and-wind-card-grid"
                cardContainerClassName="wave-and-wind-time-card"
                emptyMessage={`No wind forecast data available for ${selectedWeekday}.`}
                getCardTitle={(entry) => entry.day_segment}
                getCardSubtitle={(entry) => entry.beaufort_desc}
                getCardIcon={getCardIcon}
                headingLevel="h3"
                cardHeadingLevel="h4"
              />
            </div>
          </div>
        </>
      )}
    </Container>
  );
};

export default WaveAndWind;
